import { el, fmt, itemIcon, LABELS, todayIso } from './dom.js';
import { createPackagePicker } from './package-picker.js';

const CUSTOM = 'custom';

const cronLabel = (total) => `= ${total.toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ${total === 1 ? 'Cron' : 'Crons'}`;

// Uma linha de item recebido. Itens conhecidos usam o valor de extração da base
// do Marni (somente leitura); itens manuais aceitam Crons/unidade informados.
function createPackageItem({ pearlItems, destinations, onChange, onRemove }, initial = {}) {
  const itemSelect = el('select', { 'aria-label': 'Item' }, [
    ...pearlItems.map((item) => el('option', { value: item.id, text: item.name })),
    el('option', { value: CUSTOM, text: 'Outro item (manual)' })
  ]);
  const nameInput = el('input', { type: 'text', maxlength: '80', placeholder: 'Nome do item', 'aria-label': 'Nome do item' });
  const quantityInput = el('input', { type: 'number', min: '1', step: '1', inputmode: 'numeric', placeholder: 'Ex.: 6000', 'aria-label': 'Quantidade' });
  const destinationSelect = el('select', { 'aria-label': 'Destino' }, destinations.map((value) => el('option', { value, text: LABELS.destinations[value] || value })));
  const cronInput = el('input', { type: 'number', min: '0', step: 'any', inputmode: 'decimal', 'aria-label': 'Crons por unidade' });
  const silverInput = el('input', { type: 'number', min: '0', step: 'any', inputmode: 'decimal', placeholder: 'Silver/unidade', 'aria-label': 'Valor em Silver por unidade' });
  const total = el('output', { className: 'mc-item-total' });
  let icon = itemIcon(null);
  const row = el('div', { className: 'mc-item' }, [
    el('label', { className: 'mc-item-name' }, ['Item', el('span', { className: 'mc-item-picker' }, [icon, itemSelect]), nameInput]),
    el('label', { className: 'mc-item-qty' }, ['Qtd.', quantityInput]),
    el('label', { className: 'mc-item-dest' }, ['Destino', destinationSelect]),
    el('label', { className: 'mc-item-cron' }, ['Crons/un.', cronInput]),
    el('label', { className: 'mc-item-silver' }, ['Silver/un.', silverInput]),
    total,
    el('button', { type: 'button', className: 'mc-icon-button', title: 'Remover item', 'aria-label': 'Remover item', text: '✕', onclick: () => onRemove(api) })
  ]);

  const known = () => pearlItems.find((item) => item.id === itemSelect.value) || null;

  function sync() {
    const item = known();
    const destination = destinationSelect.value;
    if ((icon.dataset.path || '') !== (item?.iconPath || '')) {
      const next = itemIcon(item?.iconPath, { alt: item?.name || '' });
      next.dataset.path = item?.iconPath || '';
      icon.replaceWith(next);
      icon = next;
    }
    nameInput.hidden = Boolean(item);
    row.classList.toggle('is-known', Boolean(item));
    row.classList.toggle('is-extract', destination === 'extract');
    row.classList.toggle('is-sell', destination === 'sell');
    if (item) {
      cronInput.readOnly = true;
      cronInput.value = destination === 'extract' ? String(item.cronExtractionValue ?? 0) : '';
      if (destination === 'sell' && !silverInput.value && item.marketValue) silverInput.value = String(item.marketValue);
    } else {
      cronInput.readOnly = false;
    }
    cronInput.closest('label').hidden = destination !== 'extract';
    silverInput.closest('label').hidden = destination !== 'sell';
    const value = api.value();
    total.textContent = destination === 'extract'
      ? cronLabel(value.quantity * value.cronPerUnit || 0)
      : destination === 'sell' && value.silverPerUnit ? `${(value.quantity * value.silverPerUnit).toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })} Silver` : '';
  }

  const api = {
    element: row,
    isBlank: () => !(Number(quantityInput.value) > 0) && !nameInput.value.trim(),
    value() {
      const item = known();
      return {
        itemId: item ? item.id : null,
        name: item ? item.name : nameInput.value.trim(),
        quantity: Number(quantityInput.value) || 0,
        destination: destinationSelect.value,
        cronPerUnit: Number(cronInput.value) || 0,
        silverPerUnit: silverInput.value === '' ? null : Number(silverInput.value)
      };
    }
  };

  itemSelect.value = initial.itemId && pearlItems.some((item) => item.id === initial.itemId) ? initial.itemId : (initial.name ? CUSTOM : pearlItems[0]?.id || CUSTOM);
  if (initial.name && itemSelect.value === CUSTOM) nameInput.value = initial.name;
  if (initial.quantity) quantityInput.value = String(initial.quantity);
  destinationSelect.value = initial.destination || 'extract';
  if (itemSelect.value === CUSTOM && initial.cronPerUnit !== undefined) cronInput.value = String(initial.cronPerUnit || '');
  if (initial.silverPerUnit) silverInput.value = String(initial.silverPerUnit);

  for (const control of [itemSelect, destinationSelect]) control.addEventListener('change', () => { if (control === itemSelect && !known()) cronInput.value = ''; sync(); onChange(); });
  for (const control of [nameInput, quantityInput, cronInput, silverInput]) control.addEventListener('input', () => { sync(); onChange(); });
  sync();
  return api;
}

// Formulário curto: pacote, preço, desconto e itens. Região, data, tipo,
// observação e destino dos itens ficam em "Mais detalhes", com padrões
// (SA, hoje, extrair). Tudo recalcula a cada alteração; publicar é opcional.
export function createPackageForm(root, options, { onChange }) {
  const rows = [];
  const fields = {
    packageName: root.querySelector('#mcPackageName'),
    originalPrice: root.querySelector('#mcOriginalPrice'),
    discountPercent: root.querySelector('#mcDiscount'),
    region: root.querySelector('#mcRegion'),
    purchasedAt: root.querySelector('#mcPurchasedAt'),
    packageType: root.querySelector('#mcPackageType'),
    notes: root.querySelector('#mcNotes')
  };
  const itemsHost = root.querySelector('#mcItems');
  const more = root.querySelector('#mcMoreOptions');
  const form = root.querySelector('.mc-form');
  const syncAdvanced = () => form.classList.toggle('is-advanced', more.open);
  more.addEventListener('toggle', syncAdvanced);

  const PRICE_PLACEHOLDER = fields.originalPrice.placeholder;
  // Pacote do catálogo: mostra o preço de referência e, se o formulário ainda
  // estiver em branco, já preenche as Pedras Cron que vêm nele.
  const picker = createPackagePicker(fields.packageName, {
    onPick(item) {
      fields.originalPrice.placeholder = item?.referencePearls ? `ref. ${fmt.int(item.referencePearls)}` : PRICE_PLACEHOLDER;
      const cronItem = options.pearlItems.find((pearlItem) => pearlItem.category === 'CRON');
      if (item?.cronStones > 0 && cronItem && rows.every((row) => row.isBlank())) {
        rows.splice(0).forEach((row) => row.element.remove());
        addItem({ itemId: cronItem.id, quantity: item.cronStones, destination: 'extract' });
      }
      onChange();
    },
    // "+ Recebi" numa recompensa: baú que vira item de referência (Traje
    // Premium, Arma e Traje Premium…) entra como esse item; Pedra Cron (ou
    // algibeira de valor fixo) como Pedras Cron; o resto como item manual
    // guardado (sem Crons), que o jogador pode ajustar.
    onAddReward(node) {
      const cronItem = options.pearlItems.find((pearlItem) => pearlItem.category === 'CRON');
      const referenceItem = node.rewardItem && options.pearlItems.find((pearlItem) => pearlItem.id === node.rewardItem.pearlItemId);
      const fixedCrons = node.crons && node.crons.min === node.crons.max && node.crons.max > 0 && !node.crons.estimate ? node.crons.max : 0;
      let initial;
      if (referenceItem) initial = { itemId: referenceItem.id, quantity: node.quantity * (node.rewardItem.count || 1), destination: 'extract' };
      else if (cronItem && fixedCrons) initial = { itemId: cronItem.id, quantity: fixedCrons, destination: 'extract' };
      else initial = { name: node.name.slice(0, 80), quantity: node.quantity, destination: 'keep' };
      if (rows.every((row) => row.isBlank())) rows.splice(0).forEach((row) => row.element.remove());
      addItem(initial).element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      onChange();
    }
  });

  options.regions.forEach((region) => fields.region.add(new Option(LABELS.regions[region] || region, region)));
  options.packageTypes.forEach((type) => fields.packageType.add(new Option(LABELS.packageTypes[type] || type, type)));

  function addItem(initial) {
    const row = createPackageItem({
      pearlItems: options.pearlItems,
      destinations: options.destinations,
      onChange,
      onRemove: (item) => {
        rows.splice(rows.indexOf(item), 1);
        item.element.remove();
        onChange();
      }
    }, initial);
    rows.push(row);
    itemsHost.append(row.element);
    return row;
  }

  function reset() {
    fields.packageName.value = '';
    fields.originalPrice.value = '';
    fields.discountPercent.value = '0';
    fields.region.value = 'SA';
    fields.purchasedAt.value = todayIso();
    fields.packageType.value = 'PACOTE';
    fields.notes.value = '';
    fields.originalPrice.placeholder = PRICE_PLACEHOLDER;
    picker.reset();
    more.open = false;
    syncAdvanced();
    rows.splice(0).forEach((row) => row.element.remove());
    addItem();
    onChange();
  }

  function load(record) {
    fields.packageName.value = record.packageName || '';
    fields.originalPrice.value = String(record.originalPrice ?? '');
    fields.discountPercent.value = String(record.discountPercent ?? 0);
    fields.region.value = record.region || 'SA';
    fields.purchasedAt.value = record.purchasedAt || todayIso();
    fields.packageType.value = record.packageType || 'PACOTE';
    fields.notes.value = record.notes || '';
    picker.load(record.catalogPackageId);
    rows.splice(0).forEach((row) => row.element.remove());
    (record.items?.length ? record.items : [{}]).forEach((item) => addItem(item));
    // Abre os detalhes quando o registro usa algo diferente do padrão.
    more.open = Boolean(record.notes) || (record.region && record.region !== 'SA') || (record.packageType && record.packageType !== 'PACOTE')
      || (record.items || []).some((item) => item.destination && item.destination !== 'extract');
    syncAdvanced();
    onChange();
  }

  function value() {
    return {
      packageName: fields.packageName.value.trim(),
      originalPrice: Number(fields.originalPrice.value) || 0,
      discountPercent: Number(fields.discountPercent.value) || 0,
      region: fields.region.value,
      purchasedAt: fields.purchasedAt.value,
      packageType: fields.packageType.value,
      notes: fields.notes.value.trim(),
      catalogPackageId: picker.value(),
      catalogPackage: picker.selected(),
      items: rows.map((row) => row.value())
    };
  }

  Object.values(fields).forEach((field) => field.addEventListener('input', onChange));
  root.querySelector('#mcAddItem').addEventListener('click', () => { addItem().element.querySelector('select')?.focus(); onChange(); });
  reset();
  return { value, reset, load, setCommunityNames: picker.setCommunityNames };
}
